import React, { useCallback, useEffect, useState } from "react";
import { saveAs } from "file-saver";
import { getEfadaFiles, downloadEfadaFile } from "../../actions/studensts";
import AlertDenger from "../../components/AlertDenger";
import Pagination from "../Table/Pagination";
import * as FaIcons from "react-icons/fa";
import UNAUTHORIZED from "../UNAUTHORIZED";
import StatementHeader from "./StatementHeader";

// Who can use the Statement page and Statement History. Mirrors
// STATEMENT_USERS in Server/src/components/efada/efada.routes.js.
export const STATEMENT_USERS = ["admin", "asmaa", "abdelrhman"];

// Label and badge colours per letter (same colours as the Statement page's buttons).
const LETTERS = {
  efada: { label: "الافادة", badge: "bg-green-100 text-green-800 ring-green-200" },
  arabic: { label: "افادة باللغة العربية", badge: "bg-blue-100 text-blue-800 ring-blue-200" },
  mohe: { label: "MOHE Statement", badge: "bg-purple-100 text-purple-800 ring-purple-200" },
};

// "افادة_باللغة_العربية_3070….docx": the leading Arabic words are isolated as
// one right-to-left block so the browser doesn't pull the digits into them.
function FileName({ name }) {
  const m = /^([؀-ۿ]+(?:_[؀-ۿ]+)*)(.*)$/s.exec(name || "");
  if (!m) return <span dir="ltr">{name}</span>;
  return (
    <span dir="ltr">
      <bdi dir="rtl">{m[1]}</bdi>
      {m[2]}
    </span>
  );
}

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString("en-GB");
};

export default function EfadaHistory() {
  const userProfile = JSON.parse(localStorage.getItem("profile"))?.value?.[0];
  const canAccess = STATEMENT_USERS.includes(userProfile?.username);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [total, setTotal] = useState(0);
  // id of the file being downloaded, so only that row's button shows progress.
  const [downloadingId, setDownloadingId] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    const res = await getEfadaFiles(page, debouncedSearch);
    setLoading(false);
    if (res && res.status === 200) {
      setFiles(res.data.data || []);
      setMaxPage(res.data.count?.maxPage || 1);
      setTotal(res.data.count?.total || 0);
    } else {
      setFiles([]);
      setErrorMsg(res?.data?.ERROR || "Failed to load Efada history.");
      setTimeout(() => setErrorMsg(""), 3500);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    if (canAccess) fetchFiles();
  }, [fetchFiles, canAccess]);

  // Saves the original Word file under the name it was generated with.
  const handleDownload = async (file) => {
    if (downloadingId) return;
    setDownloadingId(file.id);
    const res = await downloadEfadaFile(file.id);
    setDownloadingId(null);
    if (res && res.status === 200) {
      saveAs(res.data, file.file_name);
    } else {
      setErrorMsg("Could not download this file.");
      setTimeout(() => setErrorMsg(""), 3500);
    }
  };

  if (!canAccess) return <UNAUTHORIZED />;

  const th = "px-4 py-3 font-semibold";

  return (
    <div className="px-4 pb-10 max-w-7xl mx-auto">
      {errorMsg && <AlertDenger text={errorMsg} />}

      <StatementHeader
        icon={FaIcons.FaHistory}
        title="Statement History"
        subtitle="Every statement letter generated in the system, newest first. Download any letter as the original Word file."
        link={{ to: "/main/efada", label: "New Statement", icon: FaIcons.FaFileSignature }}
      />

      <section className="bg-white rounded-xl shadow-lg">
        <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-3 border-b border-gray-100">
          <h2 className="flex items-center gap-2 text-base font-bold text-gray-800">
            <FaIcons.FaFileWord className="text-blue-800" />
            Generated Files
            <span className="ml-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
              {total}
            </span>
          </h2>
          <div className="relative w-80 max-w-full">
            <FaIcons.FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder="Search by file name, National ID or name..."
              className="border border-gray-300 rounded-lg pl-9 pr-3 py-2 w-full text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>

        <div className="p-5">
          {loading && <p className="text-gray-500 text-center py-10">Loading...</p>}

          {!loading && files.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <FaIcons.FaFolderOpen className="mx-auto text-4xl mb-2" />
              <p>{debouncedSearch ? "No files match your search." : "No statements generated yet."}</p>
            </div>
          )}

          {!loading && files.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className={th}>File Name</th>
                    <th className={th}>Letter</th>
                    <th className={th}>National ID</th>
                    <th className={th}>Student</th>
                    <th className={th}>Generated By</th>
                    <th className={th}>Generated At</th>
                    <th className={th}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {files.map((f) => {
                    const letter = LETTERS[f.letter_type];
                    return (
                      <tr key={f.id} className="hover:bg-blue-50/60 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-800 text-left">
                          <FileName name={f.file_name} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            dir="auto"
                            className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 ${
                              letter ? letter.badge : "bg-gray-100 text-gray-700 ring-gray-200"
                            }`}
                          >
                            {letter ? letter.label : f.letter_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700 font-mono text-xs">{f.national_id}</td>
                        <td className="px-4 py-3 text-gray-700" dir="auto">
                          {f.student_name || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{f.created_by || "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {formatDateTime(f.created_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => handleDownload(f)}
                            disabled={Boolean(downloadingId)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-md
                                       text-xs font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            <FaIcons.FaDownload />
                            {downloadingId === f.id ? "Downloading..." : "Download"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="pt-4">
            <Pagination
              page={page}
              setPage={setPage}
              maxPage={maxPage}
              nextDisabled={page >= maxPage}
            />
          </div>
        </div>
      </section>

    </div>
  );
}
